import React from 'react'
import { Modal, Separator, Button, Tabs } from '@/shared/ui'
import { useOportunidadeModal } from '../context/OportunidadeModalContext'
import { useOportunidadeDetails } from '../hooks/useOportunidadeDetails'
import { buildContextParts } from '../utils/contextText'
import { reportBug, getMyLatestReport, updateReport } from '@/modules/auditoria/services/auditoriaService'
import type { AuditReport } from '@/modules/auditoria/services/auditoriaService'
import { useToast } from '@/shared/ui/Toast'

export default function ReportBugModal({ open, onOpenChange }: { open: boolean; onOpenChange: (v:boolean)=>void }) {
  const { item } = useOportunidadeModal()
  const { data, asset } = useOportunidadeDetails(item)
  const [text, setText] = React.useState('')
  const [sending, setSending] = React.useState(false)
  const { add: toast } = useToast()
  const [last, setLast] = React.useState<AuditReport | null>(null)
  const [mode, setMode] = React.useState<'edit'|'new'>('new')

  React.useEffect(()=>{ if (!open) { setText(''); setMode('new'); setLast(null) } }, [open])

  React.useEffect(()=>{
    let alive = true
    async function load(){
      try {
        if (!open || !item?.id) return
        const r = await getMyLatestReport(item.id)
        if (!alive) return
        setLast(r)
        if (r) { setMode('edit'); setText(r.report_text || '') } else { setMode('new'); setText('') }
      } catch {}
    }
    load()
    return ()=> { alive = false }
  }, [open, item?.id])

  async function onSubmit() {
    try {
      if (!item?.id) throw new Error('Oportunidade inválida')
      setSending(true)
      if (mode === 'edit' && last?.id) {
        await updateReport(last.id, text)
        toast({ title: 'Relato atualizado', description: 'Seu relato foi atualizado com sucesso.', variant: 'success' })
      } else {
        await reportBug(item.id, text)
        toast({ title: 'Relato enviado', description: 'Obrigado, seu relato foi registrado.', variant: 'success' })
      }
      onOpenChange(false)
    } catch (e:any) {
      toast({ title: 'Falha ao enviar', description: e?.message || String(e), variant: 'error' })
    } finally {
      setSending(false)
    }
  }

  const ctx = React.useMemo(()=> buildContextParts(data), [data])

  // Helpers locais para exibir cabeçalho igual aos cards
  function tfShort(tfRaw?: string | null): string | null {
    const t = String(tfRaw || '').toUpperCase()
    if (!t) return null
    if (t === '5') return '5min'
    if (t === '15') return '15min'
    if (t === '30') return '30min'
    if (t === '60') return '1h'
    if (t === '240') return '4h'
    if (t === '720') return '12h'
    if (t === 'D') return '1D'
    if (t === 'W') return '1W'
    if (t === 'M') return '1M'
    return t
  }
  function minorForMajor(tfRaw?: string | null): string | null {
    const tf = String(tfRaw || '').toUpperCase()
    if (!tf) return null
    if (tf === '60') return '5'
    if (tf === '240') return '15'
    if (tf === '720') return '30'
    if (tf === 'D') return '60'
    if (tf === 'W') return '240'
    if (tf === 'M') return 'D'
    if (tf === '5' || tf === '15' || tf === '30') return tf
    return null
  }
  function toObjective(trend?: string | null): 'Long' | 'Short' | '' {
    const t = String(trend || '').toLowerCase()
    if (t === 'bullish' || t === 'alta' || t === 'long') return 'Long'
    if (t === 'bearish' || t === 'baixa' || t === 'short') return 'Short'
    return ''
  }
  function toRecommendation(row: any): string | null {
    const tf = String(row?.target_timeframe || '').toUpperCase()
    if (tf === '60') return 'Scalp Trade'
    if (tf === '240') return 'Scalp / Day Trade'
    if (tf === '720') return 'Day Trade'
    if (tf === 'D') return 'Day / Swing Trade'
    if (tf === 'W') return 'Swing Trade'
    if (tf === 'M') return 'Swing / Position Trade'
    const tf2 = String(row?.timeframe || '').toUpperCase()
    if (tf2 === '60') return 'Scalp Trade'
    if (tf2 === '240') return 'Scalp / Day Trade'
    if (tf2 === '720') return 'Day Trade'
    if (tf2 === 'D') return 'Day / Swing Trade'
    if (tf2 === 'W') return 'Swing Trade'
    if (tf2 === 'M') return 'Swing / Position Trade'
    if (tf2 === '5' || tf2 === '15' || tf2 === '30') return 'Scalp Trade'
    return null
  }
  const tfLower = tfShort(minorForMajor((data as any)?.target_timeframe) || (data as any)?.timeframe)
  const tfUpper = tfShort((data as any)?.target_timeframe)
  const recommendation = toRecommendation(data)
  const objective = toObjective(String((data as any)?.target_trend || (data as any)?.trend))

  return (
    <Modal open={open} onOpenChange={onOpenChange} className="max-w-[720px]">
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          {asset?.logo_url ? (
            <img src={asset.logo_url} alt={item?.symbol || ''} className="h-10 w-10 rounded-full object-cover" />
          ) : (
            <div className="h-10 w-10 rounded-full bg-muted" />
          )}
          <div className="min-w-0">
            <div className="text-sm font-semibold">{asset?.name || item?.name || item?.symbol}</div>
            <div className="text-xs text-foreground/70">{item?.symbol}</div>
          </div>
          <div className="ml-2 hidden flex-1 items-center justify-end gap-6 text-xs text-foreground/80 md:flex">
            <span>{tfLower || '-'} {tfLower || tfUpper ? '|' : ''} {tfUpper || '-'}</span>
            <span>{recommendation || '-'}</span>
            <span className={[objective==='Long'?'text-emerald-400':objective==='Short'?'text-red-400':'text-foreground/80','font-medium'].join(' ')}>
              {objective === 'Long' ? 'bullish' : objective === 'Short' ? 'bearish' : '-'}
            </span>
          </div>
        </div>
        <Separator />
        {ctx?.header && <div className="text-sm font-medium">{ctx.header}</div>}
        {ctx?.emasLine && <div className="text-xs text-foreground/70">{ctx.emasLine}</div>}
        {ctx?.fiboLine && <div className="text-sm text-foreground/80">{ctx.fiboLine}</div>}
        {ctx?.ema200Line && <div className="text-sm text-foreground/80">{ctx.ema200Line}</div>}
        {last && (
          <Tabs className="mt-1" tabs={[{ value: 'edit', label: 'Editar meu último relato' }, { value: 'new', label: 'Criar novo' }]} value={mode} onValueChange={(v)=>{ setMode(v as any); setText(v==='edit' ? (last?.report_text || '') : '') }} />
        )}
        <div>
          <label className="mb-1 block text-sm font-medium">Descreva o problema encontrado</label>
          <textarea
            className="h-40 w-full resize-y rounded-md border border-border bg-background p-2 text-sm outline-none focus:ring-2 focus:ring-primary/40"
            placeholder="Explique o que está errado, como reproduzir, prints, etc."
            value={text}
            onChange={e=> setText(e.target.value)}
          />
        </div>
        <div className="flex items-center justify-end gap-2">
          <Button variant="secondary" disabled={sending} onClick={()=> onOpenChange(false)}>Cancelar</Button>
          <Button disabled={sending || !text.trim()} onClick={onSubmit}>Enviar</Button>
        </div>
      </div>
    </Modal>
  )
}
